import { NonIdealState } from '@blueprintjs/core';
import { Loading } from '@hogwarts/ui-components-core';
import cn from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { VirtuosoGrid } from 'react-virtuoso';
import { rowFactory as itemFactory } from '../utils';
import styles from './styles.module.css';

type FetchMethod = (...args: any[]) => Promise<any>;
const useLastFetch = (fetch: FetchMethod): [FetchMethod, boolean] => {
  const currentInvocation = useRef(0);
  const abort = () => {
    currentInvocation.current++;
    setFetching(false);
  };
  const [fetching, setFetching] = useState(false);
  useEffect(() => {
    abort();
  }, [fetch]);
  const wrappedFetch = useCallback(
    async (...args) => {
      abort();
      const identifier = currentInvocation.current;
      setFetching(true);
      try {
        const result = await fetch(...args);
        if (currentInvocation.current !== identifier) {
          throw new Error('Aborted');
        }
        return result;
      } finally {
        if (currentInvocation.current === identifier) {
          setFetching(false);
        }
      }
    },
    [fetch]
  );

  return [wrappedFetch, fetching];
};

const DefaultEmptyComponent = () => {
  return <NonIdealState title={'No items available'} />;
};

type ItemContent = (index: number) => React.ReactElement | null;

interface InfiniteGalleryViewProps {
  className?: string;
  component: React.FunctionComponent<any>;
  componentProps?: object;
  emptyComponent?: React.ComponentType;
  spreadRowItem?: boolean;
  border?: boolean;
  divider?: boolean;
  pageSize?: number;
  useWindowScroll?: boolean;
  fetchMore: FetchMethod;
  withControlledProps?: boolean;
  /** When false, all loaded items stay mounted (searchable, no Virtuoso recycle) */
  virtualized?: boolean;
}

export const InfiniteGalleryView = ({
  className,
  component,
  componentProps,
  emptyComponent: EmptyComponent = DefaultEmptyComponent,
  spreadRowItem = false,
  border = false,
  divider = false,
  pageSize = 25,
  useWindowScroll = false,
  fetchMore: fetchMoreCore,
  withControlledProps = false,
  virtualized = true,
}: InfiniteGalleryViewProps): React.ReactElement => {
  const [items, setItems] = useState<any[] | null>();
  const [fullyLoaded, setFullyLoaded] = useState(false);
  const [fetchMore, loading] = useLastFetch(fetchMoreCore);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const Component = component;

  const controlledItem = useCallback(
    (index: number) => (
      <Component item={itemsRef.current?.[index]} {...componentProps} />
    ),
    [Component, componentProps]
  );

  const factoryItem = useMemo(
    () =>
      itemFactory(component, items || [], componentProps, {
        divider,
        spreadRowItem,
      }),
    [divider, component, componentProps, items, spreadRowItem]
  );

  const item: ItemContent = withControlledProps ? controlledItem : factoryItem;

  useEffect(() => {
    let nextRows: any;
    const fetchMoreItems = async () => {
      nextRows = await fetchMore(0, pageSize);

      if (Array.isArray(nextRows) && nextRows.length) {
        setItems([...nextRows]);
      }
    };

    setItems(null);
    setFullyLoaded(false);

    fetchMoreItems().catch(console.error);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMore]);

  const tryGetMoreItems = useCallback(async () => {
    if (loading || fullyLoaded) return;

    const currentItems = itemsRef.current;
    if (!currentItems?.length) return;

    let nextRows: any;
    try {
      nextRows = await fetchMore(currentItems.length, pageSize);
    } catch (e) {
      return console.error(e);
    }
    if (Array.isArray(nextRows)) {
      if (nextRows.length < pageSize) {
        setFullyLoaded(true);
      }
      if (nextRows.length) {
        setItems((prev) => [...(prev || []), ...nextRows]);
      }
    } else {
      setFullyLoaded(true);
    }
  }, [fetchMore, fullyLoaded, loading, pageSize]);

  useEffect(() => {
    if (virtualized || !items?.length || fullyLoaded) {
      return;
    }

    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          tryGetMoreItems();
        }
      },
      { rootMargin: '400px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [virtualized, items?.length, fullyLoaded, tryGetMoreItems]);

  if (items == null) {
    return (
      <div className={styles.footerLoading}>
        <Loading />
      </div>
    );
  }

  if (!items.length) {
    return <EmptyComponent />;
  }

  const loadedItems = items;

  const footer =
    loading && !fullyLoaded ? (
      <div className={styles.footerLoading}>
        <Loading />
      </div>
    ) : null;

  if (!virtualized) {
    return (
      <>
        <div className={cn(styles.list, border && styles.listBorders, className)}>
          {loadedItems.map((row, index) => (
            <div key={row?.id ?? index} className={styles.listItem}>
              {withControlledProps ? (
                <Component item={row} {...componentProps} />
              ) : (
                item(index)
              )}
            </div>
          ))}
        </div>
        <div ref={loadMoreSentinelRef} className={styles.loadMoreSentinel} />
        {footer}
      </>
    );
  }

  return (
    <>
      <VirtuosoGrid
        useWindowScroll={useWindowScroll}
        totalCount={loadedItems.length}
        className={cn(border && styles.listBorders, className)}
        itemContent={item}
        endReached={tryGetMoreItems}
        computeItemKey={(index) => loadedItems[index]?.id ?? index}
        overscan={{ main: 400, reverse: 200 }}
        itemClassName={styles.listItem}
        listClassName={styles.list}
      />
      {footer}
    </>
  );
};
